import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { scopedTranslation } from '@utils/I18n'
import { PageLayout } from '@components/layouts/PageLayout'
import { PageHeaderNav, type PageTabs } from '@molecules/PageHeaderNav'
import { Button } from '@atoms'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { CsvBoxNotSetUpAlert } from '@components/imports/CsvBoxNotSetUpAlert'
import { type CsvBoxEnvironment } from '@utils/imports/types'
import { EventImportsCsvBox } from '../features/import_events/EventImportsCsvBox'

const tEventsImport = scopedTranslation('events_import')

const EXAMPLE_CSV_URL = 'https://supporterbase.s3.us-east-1.amazonaws.com/example-event-and-shift-import.csv'

export type EventImportsProps = {
  licenseKey: string | null
  csvBoxEnvironment: CsvBoxEnvironment
  importToken: string
  maxShiftsCount: number
  groupNames: string[]
  eventTypeNames: string[]
  timeZoneNames: string[]
  requestingUserId: string
  locale: string
  pageTabs?: PageTabs | null
}

const resultCopy = (isSuccessful: boolean) => ({
  headline: tEventsImport(isSuccessful ? 'success_modal.headline' : 'error_modal.headline'),
  content: tEventsImport(isSuccessful ? 'success_modal.content' : 'error_modal.content'),
})

const Panel = ({
  icon,
  iconClassName,
  children,
}: {
  icon: IconTypes
  iconClassName: string
  children: React.ReactNode
}) => (
  <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:px-6 tw:py-10 tw:text-center tw:shadow-xs">
    <span
      aria-hidden="true"
      className={'tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full ' + iconClassName}
    >
      <Icon type={icon} className="tw:h-6 tw:w-6" />
    </span>
    {children}
  </div>
)

const ResultPanel = ({ isSuccessful, onReset }: { isSuccessful: boolean; onReset: () => void }) => {
  const { headline, content } = resultCopy(isSuccessful)

  return (
    <Panel
      icon={isSuccessful ? 'check-circle' : 'alert-circle'}
      iconClassName={isSuccessful ? 'tw:bg-success-50 tw:text-success-600' : 'tw:bg-error-50 tw:text-error-600'}
    >
      <div className="tw:flex tw:flex-col tw:gap-1">
        <h2 className="tw:text-base tw:font-bold tw:text-gray-cool-900">{headline}</h2>
        <p className="tw:text-sm tw:text-gray-cool-600">{content}</p>
      </div>
      <Button rank="secondary" leadingIcon="arrow-narrow-left" onClick={onReset}>
        {tEventsImport(isSuccessful ? 'import_another' : 'try_again')}
      </Button>
    </Panel>
  )
}

export default function EventImports({
  requestingUserId,
  maxShiftsCount,
  groupNames,
  eventTypeNames,
  timeZoneNames,
  licenseKey,
  csvBoxEnvironment,
  importToken,
  locale,
  pageTabs,
}: EventImportsProps) {
  const [isDone, setIsDone] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)

  const reset = () => {
    setIsDone(false)
    setIsSuccessful(false)
  }

  const announcement = isDone ? Object.values(resultCopy(isSuccessful)).join('. ') : ''

  return (
    <PageLayout.Root>
      <PageLayout.Header
        heading={tEventsImport('headline')}
        nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={Link} />}
      />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-xl tw:flex-col tw:gap-4 tw:py-6">
          <p role="status" className="tw:sr-only">
            {announcement}
          </p>

          {!licenseKey && <CsvBoxNotSetUpAlert />}

          {licenseKey && isDone && <ResultPanel isSuccessful={isSuccessful} onReset={reset} />}

          {licenseKey && !isDone && (
            <>
              <Panel icon="upload-cloud-02" iconClassName="tw:bg-primary-50 tw:text-primary-600">
                <p className="tw:max-w-sm tw:text-sm tw:text-gray-cool-600">{tEventsImport('upload_hint')}</p>
                <EventImportsCsvBox
                  requestingUserId={requestingUserId}
                  csvBoxEnvironment={csvBoxEnvironment}
                  importToken={importToken}
                  maxShiftsCount={maxShiftsCount}
                  groupNames={groupNames}
                  eventTypeNames={eventTypeNames}
                  timeZoneNames={timeZoneNames}
                  locale={locale}
                  setIsDone={setIsDone}
                  setIsSuccessful={setIsSuccessful}
                  licenseKey={licenseKey}
                />
              </Panel>

              <p className="tw:text-center tw:text-sm tw:text-gray-cool-600">
                {tEventsImport('example_csv')}{' '}
                <a
                  href={EXAMPLE_CSV_URL}
                  className="tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current"
                >
                  example-event-and-shift-import.csv
                </a>
              </p>
            </>
          )}
        </div>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
